import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useGorgiasIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-gorgias-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";
const formSchema = z.object({
  activated: z.boolean(),
  email: z.string().email(),
  apiKey: z.string(),
  domainUrl: z.string().optional(),
});

const GorgiasIntegration = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { activated, email, apiKey, domainUrl } = shop.integrations.gorgias;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, email, apiKey, domainUrl: domainUrl ?? "" },
  });

  const { mutateAsync: updateGorgiasIntegration } =
    useGorgiasIntegrationUpdateMutation(shop);

  const onSubmit = form.handleSubmit((data) => {
    updateGorgiasIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.gorgias.email")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.gorgias.gorgiasToken")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="domainUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.gorgias.domainUrl")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={!form.formState.isDirty}>
          {t("misc.save")}
        </Button>
      </form>
    </Form>
  );
};

export function GorgiasIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useGorgiasIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.gorgias) return null;

  return (
    <IntegrationPreview
      title="Gorgias"
      bgColor="#FF9780"
      textColor="black"
      logo="/integrations/gorgias.png"
      description={t("settings.integrations.thirdpart.gorgias.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.gorgias.activated}
      seeMoreUrl="https://gorgias.com"
    >
      <GorgiasIntegration />
    </IntegrationPreview>
  );
}
